import constate from 'constate';
import { useCallback, useEffect, useState } from 'react';
import { getResizer } from '../services/resizer';
import { useServices } from './services-registry';
import { useDesign } from './editor-settings';
import { useAppState } from './app-state';
import { DeviceType, ViewMode } from '../types/host-sdk';
import { getDimensions } from '../services/dimensions';
import { useWixAnalytics } from './wix-analytics';
import { useCorvidApi } from './corvid-api';

interface ProviderProps {
  initialValue?: boolean;
}

interface Context {
  isExpanded: boolean;
  setIsExpanded(boolean): void;
}

interface CollapseExpand {
  isExpanded: boolean;
  isCollapsed: boolean;
  collapse(withPopup?: boolean): void;
  expand(direction?: string, triggerBy?: string): void;
  toggle(direction?: string, triggerBy?: string): void;
}

const useContext = ({ initialValue = false }: ProviderProps): Context => {
  const [isExpanded, setIsExpanded] = useState(initialValue);
  return { isExpanded, setIsExpanded };
};

const useCollapseExpand_ = (context: Context): CollapseExpand => {
  const { hostSdk, biLogger } = useServices();
  const design = useDesign();
  const { appState } = useAppState();
  const { withQab } = appState;
  const isMobile = appState.deviceType === DeviceType.Mobile;
  const { trackExpandEvent, trackCollapseEvent } = useWixAnalytics();
  const corvidApi = useCorvidApi();

  // TODO, should not useInitChat here, circular ref
  // and other reasons: https://github.com/wix-private/inbox/pull/2545#discussion_r516525279
  // should re-think
  // const { isSdkLoaded } = useInitChat(experiments);
  const isSdkLoaded = false; // TODO: implement differently

  const isInEditingExperience = appState.viewMode === ViewMode.Editor;

  const dimensions = getDimensions(
    design,
    isInEditingExperience,
    isSdkLoaded,
    isMobile,
  );

  const collapse = useCallback(
    async (withPopup: boolean = false) => {
      if (context.isExpanded) {
        trackCollapseEvent();
        corvidApi.reportWidgetCollapsed();
      }
      context.setIsExpanded(false);
      const resizer = getResizer(withQab, isMobile, hostSdk, dimensions);
      await resizer.collapse(withPopup === true);
    },
    [context, hostSdk, corvidApi, withQab, trackCollapseEvent],
  );

  const expand = useCallback(
    async (direction?: string, triggerBy?: string) => {
      const resizer = getResizer(withQab, isMobile, hostSdk, dimensions);
      await resizer.expand();
      context.setIsExpanded(true);
      if (!context.isExpanded) {
        trackExpandEvent();
        corvidApi.reportWidgetExpand();

        if (direction && triggerBy) {
          biLogger.expandChatEvent({
            direction,
            triggerBy,
            host: appState.host,
          });
        }
      }
    },
    [context, hostSdk, corvidApi, isMobile, trackExpandEvent],
  );

  const toggle = useCallback(
    (direction?: string, triggerBy?: string) => {
      context.isExpanded ? collapse() : expand(direction, triggerBy);
    },
    [context.isExpanded, collapse, expand],
  );

  const isCollapsed = !context.isExpanded;

  useEffect(() => {
    if (isSdkLoaded && isCollapsed) {
      void collapse();
    }
  }, [isSdkLoaded]);

  return {
    isExpanded: context.isExpanded,
    isCollapsed,
    collapse,
    expand,
    toggle,
  };
};

export const [CollapseExpandProvider, useCollapseExpand] = constate(
  useContext,
  useCollapseExpand_,
);
